<template>
  <div class="ncrm-workflow-detail">
    <v-card class="ncrm-workflow-detail__header pa-4">
      <div class="d-flex flex-wrap align-center justify-space-between ga-3">
        <div class="min-w-0">
          <div class="d-flex align-center ga-2">
            <v-btn icon="mdi-arrow-left" variant="text" size="small" @click="$emit('back')" />
            <div class="text-h6 ncrm-text-strong ncrm-workflow-detail__title">
              {{ workflow.name }}
            </div>
          </div>
          <div class="text-body-2 text-medium-emphasis mt-1">
            {{ $t("ncrm.workflows.mailjetEngagement.subtitle") }}
          </div>
        </div>
        <v-btn color="primary" variant="tonal" @click="$emit('edit-workflow', workflow)">
          <v-icon start>mdi-pencil-outline</v-icon>
          {{ $t("ncrm.workflows.actions.editWorkflow") }}
        </v-btn>
      </div>
      <div class="d-flex flex-wrap ga-2 mt-4">
        <v-chip color="primary">
          {{ $t("ncrm.workflows.detail.template") }}: {{ templateName }}
        </v-chip>
        <v-chip color="info">
          {{ $t("ncrm.workflows.detail.apiSource") }}: mailjet_events → ncrm_company
        </v-chip>
        <v-chip color="neutral">
          {{ $t("ncrm.workflows.detail.version") }}: {{ workflow.version }}
        </v-chip>
      </div>
    </v-card>

    <NcrmWorkflowNodeRail
      v-model:selected-node-key="selectedNodeKey"
      :nodes="nodes"
      :api-node-key="triggerNodeKey"
      :duplicate-node-key="contactExistenceNodeKey"
      :data-enrichment-node-key="aiCompatibilityNodeKey"
      :crm-write-node-key="leadAddNodeKey"
      :lead-board-node-key="leadAddNodeKey"
      :api-summary="{}"
      :auto-sync-settings="{}"
      :dedup-auto-run-settings="{}"
      :enrichment-settings="{}"
      :crm-write-settings="{}"
      :lead-board-settings="{}"
      @edit-node="$emit('edit-node', $event)"
    />

    <v-card class="pa-5 mt-4 ncrm-workflow-detail__placeholder">
      <div class="d-flex flex-wrap align-start justify-space-between ga-3">
        <div class="d-flex align-start ga-3 min-w-0">
          <v-icon :color="nodeView ? 'primary' : 'info'" size="24">
            {{ selectedNode?.icon || "mdi-information-outline" }}
          </v-icon>
          <div class="min-w-0">
            <div class="text-subtitle-1 font-weight-semibold ncrm-text-strong">
              {{ selectedNode?.label }}
            </div>
            <div class="text-body-2 text-medium-emphasis mt-1">
              {{ selectedNode?.description }}
            </div>
          </div>
        </div>
        <div class="d-flex ga-2">
          <v-btn
            v-if="isTriggerNode"
            color="primary"
            variant="tonal"
            :loading="loadingMailjetNode"
            :disabled="loadingMailjetNode"
            @click="pickCsv"
          >
            <v-icon start>mdi-file-upload-outline</v-icon>
            {{ $t("ncrm.workflows.mailjetEngagement.importCsv") }}
          </v-btn>
          <v-btn
            v-if="isRunnableNode"
            color="primary"
            variant="flat"
            :loading="loadingMailjetNode"
            :disabled="loadingMailjetNode"
            @click="$emit('run-node', selectedNodeKey)"
          >
            <v-icon start>mdi-sync</v-icon>
            {{ $t("ncrm.workflows.mailjetEngagement.runNode") }}
          </v-btn>
        </div>
      </div>
      <input
        ref="csvInput"
        type="file"
        accept=".csv,.xlsx"
        class="d-none"
        @change="onCsvSelected"
      />

      <template v-if="nodeView">
        <v-progress-linear v-if="loadingStagingRecords && !nodeRows.length" indeterminate color="primary" height="2" class="mt-4" />
        <div v-else-if="!nodeRows.length" class="text-caption text-medium-emphasis py-6 text-center">
          {{ $t("ncrm.workflows.mailjetEngagement.emptyRows") }}
        </div>
        <v-table v-else density="comfortable" class="mt-3">
          <thead>
            <tr>
              <th class="text-left">{{ $t("ncrm.workflows.mailjetEngagement.columns.email") }}</th>
              <th class="text-left">{{ $t("ncrm.workflows.mailjetEngagement.columns.name") }}</th>
              <th class="text-left">{{ $t("ncrm.workflows.mailjetEngagement.columns.status") }}</th>
              <th class="text-left">{{ $t("ncrm.workflows.mailjetEngagement.columns.updated") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in nodeRows" :key="row.id">
              <td>{{ row.email_primary || "—" }}</td>
              <td>{{ row.name || "—" }}</td>
              <td>
                <v-chip size="x-small" :color="statusChip(row.status)">
                  {{ statusLabel(row.status) }}
                </v-chip>
              </td>
              <td class="text-caption text-medium-emphasis">{{ formatDate(row.updated_at) }}</td>
            </tr>
          </tbody>
        </v-table>
      </template>

      <v-alert v-else type="info" class="mt-3">
        {{ $t("ncrm.workflows.mailjetEngagement.skeletonNote") }}
      </v-alert>
    </v-card>
  </div>
</template>

<script>
import {
  WORKFLOW_NODE_KEYS,
  WORKFLOW_TEMPLATE_KEYS,
  createWorkflowNodeDefinitions,
} from "@/Utile/ncrmWorkflows.mjs";
import NcrmWorkflowNodeRail from "@/components/ncrm/workflows/NcrmWorkflowNodeRail.vue";
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";

const MJ_STATUS_COLORS = {
  mj_trigger_received: "info",
  mj_contact_not_found: "neutral",
  mj_pending_lead_check: "info",
  mj_lead_exists: "neutral",
  mj_pending_ai_check: "warning",
  mj_ai_incompatible: "error",
  mj_ready_for_lead: "primary",
  mj_lead_added: "success",
  mj_lead_already_exists: "success",
  mj_notified: "success",
};

// Per-node: ce rânduri (după status) afișează nodul și dacă are acțiune de rulare.
// Nodurile fără config arată placeholder-ul (sprinturile lor nu sunt încă livrate).
const MJ_NODE_VIEW = {
  "mailjet-event-trigger": { runnable: true, statuses: ["mj_trigger_received"] },
  "contact-existence-check": { runnable: true, statuses: ["mj_pending_lead_check", "mj_contact_not_found"] },
  "lead-presence-check": { runnable: true, statuses: ["mj_pending_ai_check", "mj_ready_for_lead", "mj_lead_exists"] },
  "ai-compatibility-check": { runnable: true, statuses: ["mj_pending_ai_check", "mj_ready_for_lead", "mj_ai_incompatible"] },
  "mailjet-lead-add": { runnable: true, statuses: ["mj_ready_for_lead", "mj_lead_added", "mj_lead_already_exists"] },
  "telegram-notify": { runnable: true, statuses: ["mj_lead_added", "mj_notified"] },
};

export default {
  name: "NcrmMailjetEngagementWorkflowDetail",
  components: {
    NcrmWorkflowNodeRail,
  },
  props: {
    workflow: { type: Object, required: true },
    templateName: { type: String, default: "" },
    stagingRecords: { type: Array, default: () => [] },
    loadingStagingRecords: { type: Boolean, default: false },
    loadingMailjetNode: { type: Boolean, default: false },
  },
  emits: ["back", "edit-workflow", "edit-node", "run-node", "import-csv"],
  data() {
    return {
      selectedNodeKey: WORKFLOW_NODE_KEYS.MAILJET_EVENT_TRIGGER,
    };
  },
  computed: {
    triggerNodeKey() { return WORKFLOW_NODE_KEYS.MAILJET_EVENT_TRIGGER; },
    contactExistenceNodeKey() { return WORKFLOW_NODE_KEYS.CONTACT_EXISTENCE_CHECK; },
    aiCompatibilityNodeKey() { return WORKFLOW_NODE_KEYS.AI_COMPATIBILITY_CHECK; },
    leadAddNodeKey() { return WORKFLOW_NODE_KEYS.MAILJET_LEAD_ADD; },
    isTriggerNode() { return this.selectedNodeKey === WORKFLOW_NODE_KEYS.MAILJET_EVENT_TRIGGER; },
    nodes() {
      return createWorkflowNodeDefinitions(this.$t, WORKFLOW_TEMPLATE_KEYS.MAILJET_ENGAGEMENT);
    },
    selectedNode() {
      return this.nodes.find((node) => node.key === this.selectedNodeKey) || this.nodes[0] || null;
    },
    nodeView() {
      return MJ_NODE_VIEW[this.selectedNodeKey] || null;
    },
    isRunnableNode() {
      return Boolean(this.nodeView?.runnable);
    },
    nodeRows() {
      const statuses = this.nodeView?.statuses || [];
      if (!statuses.length) return [];
      return (this.stagingRecords || []).filter((row) => statuses.includes(row.status));
    },
  },
  methods: {
    pickCsv() {
      this.$refs.csvInput?.click();
    },
    onCsvSelected(event) {
      const file = event?.target?.files?.[0];
      if (file) this.$emit("import-csv", file);
      if (event?.target) event.target.value = "";
    },
    statusChip(status) {
      return MJ_STATUS_COLORS[status] || "neutral";
    },
    statusLabel(status) {
      const key = `ncrm.workflows.mailjetEngagement.statuses.${status}`;
      const label = this.$t(key);
      return label === key ? String(status || "").replace(/_/g, " ") : label;
    },
    formatDate(value) {
      if (!value) return "—";
      return formatDisplayDateTime(value);
    },
  },
};
</script>

<style src="./NcrmWorkflowDetail.css" scoped></style>
